import { useEffect, useState } from 'react';
import { confirmMonth, getConfirmMonthPreview, ConfirmMonthPreview, EvidenceSource } from '../api/adminApi';
import { getApproximateHijriToday } from '../utils/approximateHijri';
import ConfirmMonthChecklist from './ConfirmMonthChecklist';
import ResetLocationPanel from './ResetLocationPanel';
import styles from './InitializeModal.module.css';

const HIJRI_MONTHS = [
  '', 'Muharram', 'Safar', "Rabi' al-Awwal", "Rabi' al-Thani",
  'Jumada al-Awwal', 'Jumada al-Thani', 'Rajab', "Sha'ban",
  'Ramadan', 'Shawwal', "Dhu al-Qi'dah", 'Dhu al-Hijjah',
];

interface Props {
  locationCode: string;
  locationName: string;
  onClose: () => void;
  onSuccess: () => void;
}

export default function InitializeModal({ locationCode, locationName, onClose, onSuccess }: Props) {
  const today = getApproximateHijriToday();
  const [year, setYear] = useState(today.year);
  const [month, setMonth] = useState(today.month.index);
  const [startDate, setStartDate] = useState('');
  const [sources, setSources] = useState<EvidenceSource[]>([{ url: '', language: 'ar', comment: '' }]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const [preview, setPreview] = useState<ConfirmMonthPreview | null>(null);
  const [previewLoading, setPreviewLoading] = useState(true);

  const yearOptions = [today.year - 1, today.year, today.year + 1];

  async function refreshPreview() {
    setPreviewLoading(true);
    try {
      setPreview(await getConfirmMonthPreview(locationCode, year, month));
    } catch {
      // If the preview fails (e.g. transient network issue) fall back to letting the
      // normal confirm flow surface the invariant error, rather than blocking the modal.
      setPreview(null);
    } finally {
      setPreviewLoading(false);
    }
  }

  useEffect(() => {
    refreshPreview();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locationCode, year, month]);

  function addSource() {
    setSources(s => [...s, { url: '', language: 'ar', comment: '' }]);
  }
  function removeSource(i: number) {
    setSources(s => s.filter((_, idx) => idx !== i));
  }
  function updateSource(i: number, field: keyof EvidenceSource, value: string) {
    setSources(s => s.map((src, idx) => idx === i ? { ...src, [field]: value } : src));
  }

  async function handleSubmit() {
    if (!startDate) { setError('Please enter a start date.'); return; }
    setSubmitting(true);
    setError('');
    try {
      const validSources = sources.filter(s => s.url.trim());
      await confirmMonth({
        locationCode,
        year,
        month,
        startDate,
        source: validSources.length > 0 ? validSources[0].url : 'Manual initialization',
        sources: validSources.length > 0 ? validSources : undefined,
      });
      onSuccess();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setSubmitting(false);
    }
  }

  const blockedByGap = !previewLoading && preview !== null && !preview.canConfirmDirectly;

  const resetConfirmArgs = startDate
    ? (() => {
        const validSources = sources.filter(s => s.url.trim());
        return {
          year,
          month,
          startDate,
          source: validSources.length > 0 ? validSources[0].url : 'Manual initialization',
          sources: validSources.length > 0 ? validSources : undefined,
        };
      })()
    : null;

  return (
    <div className={styles.overlay} onClick={e => e.target === e.currentTarget && onClose()}>
      <div className={styles.modal}>
        <div className={styles.modalHeader}>
          <div>
            <h2 className={styles.title}>Initialize {locationName}</h2>
            <p className={styles.subtitle}>Confirm the start date of a Hijri month to initialize this country.</p>
          </div>
          <button className={styles.closeBtn} onClick={onClose}>✕</button>
        </div>

        <div className={styles.body}>
          <section className={styles.section}>
            <h3 className={styles.sectionTitle}>1 — Month</h3>
            <div className={styles.monthRow}>
              <select className={styles.select} value={year} onChange={e => setYear(Number(e.target.value))}>
                {yearOptions.map(y => <option key={y} value={y}>{y} AH</option>)}
              </select>
              <select className={styles.select} value={month} onChange={e => setMonth(Number(e.target.value))}>
                {HIJRI_MONTHS.slice(1).map((name, i) => (
                  <option key={i + 1} value={i + 1}>{name}</option>
                ))}
              </select>
            </div>
          </section>

          {!previewLoading && preview && preview.missingMonths.length > 0 && (
            <section className={styles.section}>
              <h3 className={styles.sectionTitle}>Confirmation gap found</h3>
              <ConfirmMonthChecklist
                locationCode={locationCode}
                missingMonths={preview.missingMonths}
                onConfirmed={refreshPreview}
                hint={
                  <>
                    {locationName} has a confirmed month on record for {preview.previousConfirmed?.monthName.en} {preview.previousConfirmed?.year}
                    {' '}(started {preview.previousConfirmed?.startDate}). Months must be confirmed in order — confirm the following first,
                    or use "Start fresh" below to discard prior history.
                  </>
                }
              />
            </section>
          )}

          <section className={styles.section}>
            <h3 className={styles.sectionTitle}>2 — Start Date</h3>
            <label className={styles.fieldLabel}>
              When did {HIJRI_MONTHS[month]} {year} officially start?
            </label>
            <input
              type="date"
              className={styles.input}
              value={startDate}
              onChange={e => setStartDate(e.target.value)}
            />
          </section>

          <section className={styles.section}>
            <div className={styles.sectionHeaderRow}>
              <h3 className={styles.sectionTitle}>3 — Sources / Evidence</h3>
              <button className={styles.addSourceBtn} type="button" onClick={addSource}>+ Add source</button>
            </div>
            <div className={styles.sourcesList}>
              {sources.map((src, i) => (
                <div key={i} className={styles.sourceRow}>
                  <input
                    className={styles.input}
                    type="url"
                    placeholder="https://example.com/announcement"
                    value={src.url}
                    onChange={e => updateSource(i, 'url', e.target.value)}
                  />
                  <select
                    className={styles.select}
                    value={src.language}
                    onChange={e => updateSource(i, 'language', e.target.value)}
                  >
                    <option value="ar">Arabic</option>
                    <option value="fr">French</option>
                    <option value="en">English</option>
                  </select>
                  <input
                    className={styles.input}
                    placeholder="Comment (optional)"
                    value={src.comment ?? ''}
                    onChange={e => updateSource(i, 'comment', e.target.value)}
                  />
                  {sources.length > 1 && (
                    <button className={styles.removeSourceBtn} type="button" onClick={() => removeSource(i)}>✕</button>
                  )}
                </div>
              ))}
            </div>
          </section>

          {error && <div className={styles.error}>{error}</div>}

          <div className={styles.actions}>
            <button className={styles.cancelBtn} onClick={onClose} disabled={submitting}>Cancel</button>
            <button
              className={styles.confirmBtn}
              onClick={handleSubmit}
              disabled={!startDate || submitting || blockedByGap}
              title={blockedByGap ? 'Confirm the missing months above first, or use "Start fresh" below' : undefined}
            >
              {submitting ? 'Initializing…' : 'Initialize'}
            </button>
          </div>

          {!previewLoading && preview && preview.previousConfirmed && (
            <ResetLocationPanel
              locationCode={locationCode}
              locationName={locationName}
              deletingMonthLabel={`${preview.previousConfirmed.monthName.en} ${preview.previousConfirmed.year}`}
              targetLabel={`${HIJRI_MONTHS[month]} ${year}`}
              confirmArgs={resetConfirmArgs}
              onSuccess={onSuccess}
            />
          )}
        </div>
      </div>
    </div>
  );
}
